import {
  ColumnType,
  GeneratedAlways,
  Insertable,
  Selectable,
  Updateable,
} from 'kysely';
import {
  CampaignFrequency,
  CampaignStatus,
  CampaignCoverageTier,
  LivestockInsuranceTypes,
  RmaLgmTypeCode,
  RmaLrpTypeCode,
  RmaLgmCommodityCode,
  RmaLrpCommodityCode,
  LgmCampaignPdfColumn,
  LrpCampaignPdfColumn,
  RmaLrpCommodityCodes,
  RmaLgmCommodityCodes,
  RmaLrpTypeCodes,
  RmaLgmTypeCodes,
  LrpCampaignPdfColumns,
  LgmCampaignPdfColumns,
} from '@harvestiq/constants';
import type {
  DbCalendarDateNullable,
  DbColumnDecimalNullable,
  DbTimestampGenerated,
  DbTimestampNullable,
} from '@harvestiq/utils';
import {
  CalendarDate,
  z,
  zCalendarDate,
  zDecimalNullable,
  zTimestamp,
} from '@harvestiq/zod';
import { expectTypeOf } from 'expect-type';

export interface CampaignTable {
  id: GeneratedAlways<string>;
  ownerId: ColumnType<string, string, never>; // userId uuid, cannot update
  name: string;
  status: CampaignStatus;
  frequency: CampaignFrequency; // enum
  frequencyDays: number[] | null; // 0-6
  insuranceType: LivestockInsuranceTypes;
  commodity: RmaLgmCommodityCode | RmaLrpCommodityCode;
  typeCode: RmaLgmTypeCode | RmaLrpTypeCode;
  marketingMonths: number[] | null; // 0-11
  marketingLengths: number[] | null; // 2-11
  deductibles: number[] | null;
  targetWeight: DbColumnDecimalNullable;
  numberOfHead: number | null;
  coverageTier: CampaignCoverageTier | null;
  liveCattleTargetCwtPerHead: DbColumnDecimalNullable;
  feederCattleTargetCwtPerHead: DbColumnDecimalNullable;
  cornTargetBuPerHead: DbColumnDecimalNullable;
  endDate: DbCalendarDateNullable;
  copyOwner: boolean | null;
  emailNotes: string | null;
  quoteColumns: (LrpCampaignPdfColumn | LgmCampaignPdfColumn)[];
  createdAt: DbTimestampGenerated;
  updatedAt: DbTimestampGenerated;
  deletedAt: DbTimestampNullable;
  sentAt: DbTimestampNullable;
  lastSed: CalendarDate | null;
  s3Filename: string | null | undefined;
  smsSentAt: DbTimestampNullable;
  isPre2026BeginningFarmerRancher: boolean;
  isVeteranFarmerRancher: boolean;
  beginningFarmerRancherCropYearCount: number | null;
}
export type DbCampaign = Selectable<CampaignTable>;
export type DbCampaignInsert = Insertable<CampaignTable>;
export type DbCampaignUpdate = Updateable<CampaignTable>;

export const dbCampaignSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid(),
  name: z.string(),
  status: z.nativeEnum(CampaignStatus),
  frequency: z.nativeEnum(CampaignFrequency),
  frequencyDays: z.array(z.number().int().min(0).max(6)).nullable().optional(),
  insuranceType: z.nativeEnum(LivestockInsuranceTypes),
  commodity: z.union([
    z.nativeEnum(RmaLrpCommodityCodes),
    z.nativeEnum(RmaLgmCommodityCodes),
  ]),
  typeCode: z.union([
    z.nativeEnum(RmaLrpTypeCodes),
    z.nativeEnum(RmaLgmTypeCodes),
  ]),
  targetWeight: zDecimalNullable(),
  liveCattleTargetCwtPerHead: zDecimalNullable(),
  feederCattleTargetCwtPerHead: zDecimalNullable(),
  cornTargetBuPerHead: zDecimalNullable(),
  numberOfHead: z.number().int().min(1).nullable(),
  marketingMonths: z
    .array(z.number().int().min(0).max(11))
    .nullable()
    .optional(),
  marketingLengths: z
    .array(z.number().int().min(2).max(11))
    .nullable()
    .optional(),
  deductibles: z.array(z.number().int().min(0).max(150)).nullable(), // TODO: validity check
  coverageTier: z.nativeEnum(CampaignCoverageTier).nullable(),
  endDate: zCalendarDate().nullable(),
  copyOwner: z.boolean().nullable(),
  emailNotes: z.string().nullable(),
  quoteColumns: z.array(
    z.nativeEnum(LrpCampaignPdfColumns).or(z.nativeEnum(LgmCampaignPdfColumns))
  ),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  lastSed: zCalendarDate().nullable().optional(),
  s3Filename: z.string().nullable().optional(),
  smsSentAt: z.coerce.date().nullable().optional(),
  isPre2026BeginningFarmerRancher: z.boolean(),
  isVeteranFarmerRancher: z.boolean(),
  beginningFarmerRancherCropYearCount: z.number().int().nullable(),
});
type DbCampaignSchemaType = z.output<typeof dbCampaignSchema>;

expectTypeOf<DbCampaign>().toMatchTypeOf<DbCampaignSchemaType>();
expectTypeOf<keyof DbCampaignSchemaType>().toEqualTypeOf<keyof DbCampaign>();

export const dbCampaignInsertSchema = dbCampaignSchema
  .omit({
    id: true,
    frequencyDays: true,
  })
  .partial()
  .extend({
    ownerId: z.string().uuid(),
    name: z.string(),
    status: z.nativeEnum(CampaignStatus),
    frequency: z.nativeEnum(CampaignFrequency),
    frequencyDays: z
      .array(z.number().int().min(0).max(6))
      .nullable()
      .optional(),
    insuranceType: z.nativeEnum(LivestockInsuranceTypes),
    commodity: z.union([
      z.nativeEnum(RmaLrpCommodityCodes),
      z.nativeEnum(RmaLgmCommodityCodes),
    ]),
    typeCode: z.union([
      z.nativeEnum(RmaLrpTypeCodes),
      z.nativeEnum(RmaLgmTypeCodes),
    ]),
    targetWeight: zDecimalNullable().optional(),
    numberOfHead: z.number().int().min(1).nullable().optional(),
    liveCattleTargetCwtPerHead: zDecimalNullable().optional(),
    feederCattleTargetCwtPerHead: zDecimalNullable().optional(),
    cornTargetBuPerHead: zDecimalNullable().optional(),
    marketingMonths: z
      .array(z.number().int().min(0).max(11))
      .nullable()
      .optional(),
    marketingLengths: z
      .array(z.number().int().min(2).max(11))
      .nullable()
      .optional(),
    deductibles: z
      .array(z.number().int().min(0).max(150))
      .nullable()
      .optional(), // TODO: validity check
    coverageTier: z.nativeEnum(CampaignCoverageTier).nullable().optional(),
    endDate: zCalendarDate().nullable().optional(),
    copyOwner: z.boolean().nullable().optional(),
    emailNotes: z.string().nullable().optional(),
    quoteColumns: z.array(
      z
        .nativeEnum(LrpCampaignPdfColumns)
        .or(z.nativeEnum(LgmCampaignPdfColumns))
    ),
    createdAt: zTimestamp().optional(),
    updatedAt: zTimestamp().optional(),
    deletedAt: zTimestamp().nullable().optional(),
    sentAt: zTimestamp().nullable().optional(),
    lastSed: zCalendarDate().nullable().optional(),
    s3Filename: z.string().nullable().optional(),
    smsSentAt: zTimestamp().nullable().optional(),
    isPre2026BeginningFarmerRancher: z.boolean().optional().default(false),
    isVeteranFarmerRancher: z.boolean().optional().default(false),
    beginningFarmerRancherCropYearCount: z.number().int().nullable().optional(),
  });
type DbInsertCampaignSchemaType = z.input<typeof dbCampaignInsertSchema>;

expectTypeOf<DbCampaignInsert>().toMatchTypeOf<DbInsertCampaignSchemaType>();
expectTypeOf<keyof DbInsertCampaignSchemaType>().toEqualTypeOf<
  keyof DbCampaignInsert
>();

export const dbCampaignUpdateSchema = dbCampaignInsertSchema
  .omit({ ownerId: true })
  .partial();
type DbUpdateCampaignSchemaType = z.input<typeof dbCampaignUpdateSchema>;
expectTypeOf<DbCampaignUpdate>().toMatchTypeOf<DbUpdateCampaignSchemaType>();
expectTypeOf<keyof DbUpdateCampaignSchemaType>().toEqualTypeOf<
  keyof DbCampaignUpdate
>();
